// UI
import { Form, Input, Modal, message } from 'antd';
// Globals
import gql from 'graphql-tag';
import React from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';

// Locals
import { addStep, setNewStepModalVisible } from '../actions';
import type { CreateStepInput, CreateStepMutation } from '../graphql/api';
import type { IState, Step } from '../graphql/types';
import useMutation from '../hooks/useMutation';
import useGiide from '../hooks/useQuery/useGiide';
// Types
import type { GiideRouteParams } from '../types';
import { formInputLength } from './utils';

const createStep = gql`
  mutation CreateStep($input: CreateStepInput!) {
    createStep(input: $input) {
      id
      owner
      tenantID
    }
  }
`;

const NewStep: React.FC = () => {
  const [form] = Form.useForm();
  const { push } = useHistory();
  const { pathname } = useLocation();
  const { id: giideId } = useParams<GiideRouteParams>();
  if (!giideId) {
    push('/');
  }
  const { steps, data } = useGiide(giideId);
  const dispatch = useDispatch();
  const visible = useSelector<IState, boolean>(state => !!state.newStepModal);
  const [loading, setLoading] = React.useState<boolean>(false);

  const { callMutation } = useMutation<CreateStepInput, CreateStepMutation>(
    createStep
  );

  const saveNewStep = React.useCallback(async () => {
    try {
      await form.validateFields();
    } catch (e) {
      return;
    }

    if (!loading) {
      setLoading(true);
    }
    const title = form.getFieldValue('name');
    const order = (steps?.[steps.length - 1]?.order ?? -1) + 1;
    let result;

    try {
      result = await callMutation({
        stepGiideId: data!.id,
        title,
        order,
      });
    } catch (e) {
      setLoading(false);
      message.error('Error occurred while saving step');
    }

    setLoading(false);
    if (result) {
      batch(() => {
        dispatch(setNewStepModalVisible(false));
        dispatch(addStep({ ...result.data.createStep, title, order } as Step));
      });

      form.setFieldsValue({ name: '' });
      if (pathname.endsWith('/submit')) {
        push(pathname.substr(0, pathname.length - 7));
      }
    }
  }, [loading, form, steps, callMutation, data, dispatch, pathname, push]);

  const handleOk = React.useCallback(
    e => {
      e.preventDefault();
      saveNewStep();
    },
    [saveNewStep]
  );

  const handleCancel = React.useCallback(() => {
    if (!loading) {
      form.resetFields();
      dispatch(setNewStepModalVisible(false));
    }
  }, [loading, form, dispatch]);

  return (
    <Form onFinish={handleOk} form={form} name="newStep">
      <Modal
        centered
        title="Name of Chapter"
        open={visible}
        onCancel={handleCancel}
        onOk={handleOk}
        closable={false}
      >
        <Form.Item
          name="name"
          validateFirst
          getValueFromEvent={e => formInputLength(e.target.value, 50)}
          rules={[
            {
              required: true,
              message: 'Please input chapter name',
            },
            { max: 50, message: 'Limit of 50 characters exceeded' },
          ]}
          validateTrigger="onChange"
        >
          <Input
            size={'large'}
            autoFocus
            onPressEnter={loading ? undefined : saveNewStep}
          />
        </Form.Item>
      </Modal>
    </Form>
  );
};

export default NewStep;
